import React from 'react';
import PropTypes from 'prop-types';
import { Card, Collapse, constants } from 'day8-ui';
import pluralize from 'pluralize';


import { isCabinBooking, isStandardBooking } from '../../helpers/booking';
import MaterialIconList from '../MaterialIconList';
import { PACKAGES, DESTINATION } from '../../constants';
import {
  isUltraFriday,
  isUltraWeekend,
  numberOfNights,
  isPrivateCharterWeek,
  isSignaturerWeek,
  isClassicWeek,
  isYachtsAndFriends
} from '../../helpers/week';
import { capitalizeFirstLetter } from '../../helpers/utilities';
import { isIsMooringIncluded } from '../../helpers/pricing';

const { TYW_DESTINATION, CUSTOM_BOOKING_DESTINATIONS } = constants;

export const includedItems = (isStandardYacht, packageData, destinationId, cabinCount, soloCount, week, isYachtsAndFriends, selectedStandardYachtWeek, excludeStaffItems) => isYachtsAndFriends ?ynfIncludedItems(week, destinationId) :tywIncludedItems(isStandardYacht, packageData, destinationId, cabinCount, soloCount, week, selectedStandardYachtWeek, excludeStaffItems);

const ynfIncludedItems = (week, destinationId) => {
  const itemArray = [];
  if (isClassicWeek(week)) {
    const items = [
      "Your week's accommodation on board the yacht",
      'Professional Skipper',
      'Your chef to prepare your meals',
      '6 breakfasts, 6 lunches and 3 dinners on board your yacht',
      'Local beer and wine to accompany your meals on board',
      'Welcome dinner at a local restaurant',
      'Hikes and daily tours',
      'Max 50 travellers per group (8 per yacht)',
      '2 destination specific activities',
      'Snorkeling gear',
      'Final cleaning of your yacht',
      'Towels & bed linen',
      'Dinghy & outboard engine',
      'Long lasting memories'
    ];
    itemArray.push(...items);
  }
  if (isSignaturerWeek(week)) {
    const items = [
      "Your week's accommodation on board the yacht",
      'Professional Skipper',
      'Your chef to prepare your meals',
      `6 breakfasts, 6 lunches and 3 dinners ${destinationId !== TYW_DESTINATION.YNF_SALT_ESCAPES_BVI ? 'on board your yacht' : 'throughout the week'}`,
      'Hikes and daily tours',
      destinationId !== TYW_DESTINATION.YNF_SALT_ESCAPES_BVI ? 'Daily yoga and sound healing sessions' : 'Full fitness programme including a minimum of one fitness activity per day',
      `Max ${destinationId !== TYW_DESTINATION.YNF_SALT_ESCAPES_BVI ? '60' : '44'} travellers per group (8 per yacht)`,
      '2 destination specific activities',
      'Snorkeling gear',
      'Final cleaning of your yacht',
      'Towels & bed linen',
      'Dinghy & outboard engine',
      'Long lasting memories'
    ];
    itemArray.push(...items);
    if (destinationId !== TYW_DESTINATION.YNF_SALT_ESCAPES_BVI) {
      itemArray.push('Local beer and wine to accompany your meals on board');
    }
  }
  if (isPrivateCharterWeek(week)) {
    const items = [
      "Your week's accommodation on board the yacht",
      'Professional Skipper',
      'Snorkeling gear',
      'Final cleaning of your yacht',
      'Towels & bed linen',
      'Dinghy & outboard engine',
      'Sound System',
      'Yacht equipment',
      'GPS & nautical charts',
      'Long lasting memories'
    ];
    itemArray.push(...items);
  }

  if (!isPrivateCharterWeek(week) && !DESTINATION.NOT_WIFI.includes(destinationId)) {
    itemArray.push('WIFI (sometimes – it is the ocean after all!)');
  }
  return itemArray;
};
const tywIncludedItems = (isStandardYacht, packageData, destinationId, cabinCount, soloCount, week, selectedStandardYachtWeek, excludeStaffItems) => {
  const numNights = week ? numberOfNights(week) : 7;
  const itemArray = [];
  if (CUSTOM_BOOKING_DESTINATIONS.includes(destinationId)) {
    return [];
  }
  if (!excludeStaffItems) {
    itemArray.push("Professional Skipper");
  }
  const sharedItems = [
    `${numNights + 1} days and ${numNights} nights`,
    'Access to all events',
    'Sound System',
    'Dinghy & outboard engine',
    'Yacht equipment',
    'GPS & nautical charts',
    'Towels & bed linen',
    'Final cleaning'
  ];
  itemArray.push(...sharedItems);
  const isMooringIncluded = isIsMooringIncluded(week?.id);
  if (isMooringIncluded) {
    itemArray.push('Fuel & mooring');
  }
  if (isStandardYacht) {
    if (cabinCount && !excludeStaffItems) {
      itemArray.push(`2-person ${pluralize('cabin', cabinCount)} on a ${packageData.name} yacht`);
    }
    if (soloCount && !excludeStaffItems) {
      itemArray.push(`${pluralize('Spot', soloCount)} in shared 2-person cabin${soloCount > 2 ? 's' : ''}
        on a ${packageData.name} yacht`);
    }
    if (!cabinCount && !soloCount && !excludeStaffItems) {
      itemArray.push(`${capitalizeFirstLetter(packageData.name)} yacht`);
    }
    if (!excludeStaffItems) {
      // Basic items which are on all packages
      if (selectedStandardYachtWeek?.standardYacht.host) {
        itemArray.push("Professional Host");
      }
    }
    // CABINS specific
    if (cabinCount || soloCount) {
      if (![PACKAGES.CLASSIC_MONO, PACKAGES.CLASSIC].includes(packageData.id)) {
        const cabinExtraItems = [
          `6 full spread breakfasts`,
          `6 prepared lunches`,
          '2 dinners'
        ];
        if (!excludeStaffItems) {
          cabinExtraItems.push("Professional Host");
        }
        itemArray.push(...cabinExtraItems);
      }
      itemArray.push("Free date changes");
    } else {
      // PACKAGES YACHTS specific
    }
  } else {
    // Custom Yacht specific:
  }
  //
  // Custom stuff per destination - apply an both packages and custom yachts
  //
  if (isUltraFriday(week, destinationId)) {
    itemArray.push('Friday VIP Ultra tickets');
  }
  if (isUltraWeekend(week, destinationId)) {
    itemArray.push('Saturday and Sunday VIP Ultra tickets');
  }
  if (!DESTINATION.NOT_WIFI.includes(destinationId)) {
    itemArray.push('WIFI');
  }
  return itemArray;
};

const WhatsIncluded = ({ booking, className }) => {
  const isStandardYacht = isStandardBooking(booking);
  const isCabin = isCabinBooking(booking);
  let packageData;
  if (isCabin) packageData = booking.package;
  if (isStandardYacht) packageData = booking?.standardYachtWeek?.standardYacht?.package;

  let cabinCount = 0;
  let soloCount = 0;
  if (isCabin) {
    booking.bookedCabinWeeks.forEach(bcw => {
      if (bcw.cabinWeek.cabin.persons === 1) {
        soloCount += 1;
      } else {
        cabinCount += 1;
      }
    });
  }
  const items = includedItems(isCabin || isStandardYacht, packageData, booking.week.destination.id, cabinCount, soloCount, booking.week, isYachtsAndFriends(booking.week));
  return (
    <Card className={className} cardType='BORDERED'>
      <Collapse title={<h3 className='dui-font-body'>What&apos;s included</h3>}>
        <div className='dui-mt-4'>
          <MaterialIconList icon={{ color: 'green', name: 'done' }} items={items} />
        </div>
      </Collapse>
    </Card>
  );
};

WhatsIncluded.propTypes = {
  booking: PropTypes.object.isRequired
};

export default WhatsIncluded;
